// @file Store for the new-signup recommended actions card on the Getting started tab: whether it's shown and
// its reload cursor from the dashboard starting state, the padlet.tv sessions it recommends from, and the active
// day and reload total its analytics report.
import currentUser from '@@/bits/current_user'
import { captureFetchException } from '@@/bits/error_tracker'
import { buildUserIdCacheKey, fetchCachedQuery } from '@@/bits/query_client'
import type { PadletTvSession, PinnedPdAction, RecommendedAction } from '@@/bits/recommended_actions_card'
import { RecommendedActionsCard as RecommendedActionsCardApi } from '@@/dashboard/padlet_api'
import { FetchJsonStatus, RecommendedActionsCardExperimentVariant } from '@@/enums'
import { Ab as AbApi } from '@@/payment/padlet_api'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import { useDashMakeStore } from '@@/pinia/dash_make_store'
import { useExperimentsStore } from '@@/pinia/experiments'
import type { JsonApiResponse } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

const EXPERIMENT_NAME = 'recommended_actions_card_experiment'
type RecommendedActionsCardGoal = 'viewed_home' | 'clicked_action' | 'dismissed'
// viewed_home is the arms' shared denominator, and only the dashboard load sends it, so a failed attempt is tried
// again after each of these delays.
const VIEWED_HOME_RETRY_DELAYS_MS = [5_000, 30_000]

const useDashRecommendedActionsCardStore = defineStore('dashRecommendedActionsCardStore', () => {
  const dashAccountsStore = useDashAccountsStore()
  const dashMakeStore = useDashMakeStore()
  const experimentsStore = useExperimentsStore()

  // Whether the server shows the card to this user at all; see RecommendedActions (Rails).
  const xRecommendedActionsCard = ref<boolean>(false)
  // From the server, which counts active days and reloads. Only the card's analytics read these; the cursor
  // decides what it shows.
  const activeDay = ref<number>(1)
  const reloads = ref<number>(0)
  // The key of the action the last reload featured; null before the first reload. See chooseRecommendedActions.
  const cursor = ref<string | null>(null)

  const padletTvSessions = ref<PadletTvSession[]>([])
  const padletTvSessionsStatus = ref<FetchJsonStatus>(FetchJsonStatus.NotStarted)
  // Shared by callers that overlap, so each waits for the sessions.
  let padletTvSessionsRequest: Promise<void> | null = null
  // viewed_home is sent once per page load. Set while the request is in flight too, so it's sent once; cleared
  // if it fails, to try again.
  const hasConvertedViewedHome = ref<boolean>(false)
  let viewedHomeRetries = 0
  // The actions the card shows and whether its view has been tracked, kept for the page load rather than per
  // component, so they stay the same however many times the card is mounted.
  const shownSequences = shallowRef<{
    videos: RecommendedAction[]
    remakes: RecommendedAction[]
    pdAction: PinnedPdAction | null
  } | null>(null)
  const hasTrackedView = ref<boolean>(false)
  // The last reload's save. Each save waits for the one before, so the server keeps the latest cursor.
  let pendingReloadSave: Promise<void> = Promise.resolve()

  // Not where the user can't make padlets. In their personal account, a user at their padlet limit still
  // qualifies (the Gallery's Create prompts an upgrade), unless their tenant blocks it.
  const canMakePadlets = computed<boolean>(() =>
    dashAccountsStore.currentLibrary == null
      ? dashAccountsStore.currentUserOptions?.canMakeWallInTenant !== false
      : !dashMakeStore.cannotMakeWallInCurrentAccount,
  )
  // Not where the Gallery tab is hidden.
  const canViewGallery = computed<boolean>(
    () => dashAccountsStore.currentUserOptions?.canViewGallery === true && dashAccountsStore.canViewCrossLibraryGallery,
  )

  // Not a library the user owns or administers: that library keeps Getting started for its own onboarding, so no
  // page shows both. A school's owners and admins are only known once its memberships load, so a library waits.
  const isOutsideOnboardingLibrary = computed<boolean>(
    () =>
      dashAccountsStore.currentLibrary == null ||
      (dashAccountsStore.hasFetchedLibraryOnboardingData && !dashAccountsStore.canCurrentUserOnboardOntoLibrary),
  )

  // In the experiment, in any arm (control included), and in the current account able to make padlets and see the
  // Gallery, outside a library they own or administer. viewed_home is sent only to these users, so the arms are
  // compared over the same users.
  const isExposedInCurrentAccount = computed<boolean>(
    () =>
      xRecommendedActionsCard.value &&
      experimentsStore.recommendedActionsCardExperimentVariant != null &&
      canMakePadlets.value &&
      canViewGallery.value &&
      isOutsideOnboardingLibrary.value,
  )

  // Exposed (above) and in an arm that shows the card. The dashboard decides which pages show it.
  const xRecommendedActionsCardInCurrentAccount = computed<boolean>(
    () =>
      isExposedInCurrentAccount.value &&
      [
        RecommendedActionsCardExperimentVariant.VideoFirst,
        RecommendedActionsCardExperimentVariant.GalleryFirst,
      ].includes(experimentsStore.recommendedActionsCardExperimentVariant as RecommendedActionsCardExperimentVariant),
  )

  function initialize(): void {
    const startingState = (window.ww?.vueStartingState ?? {}) as {
      showRecommendedActionsCard?: boolean
      recommendedActionsCardActiveDay?: number | null
      recommendedActionsCardReloads?: number | null
      recommendedActionsCardCursor?: string | null
    }
    xRecommendedActionsCard.value = startingState.showRecommendedActionsCard === true
    activeDay.value = startingState.recommendedActionsCardActiveDay ?? 1
    reloads.value = startingState.recommendedActionsCardReloads ?? 0
    cursor.value = startingState.recommendedActionsCardCursor ?? null
  }

  async function fetchPadletTvSessions(): Promise<void> {
    if (padletTvSessionsStatus.value === FetchJsonStatus.NotStarted) padletTvSessionsRequest = requestPadletTvSessions()
    await padletTvSessionsRequest
  }

  async function requestPadletTvSessions(): Promise<void> {
    padletTvSessionsStatus.value = FetchJsonStatus.Fetching
    try {
      await fetchCachedQuery<JsonApiResponse<PadletTvSession>>({
        cacheKey: ['fetchPadletTvSessions', buildUserIdCacheKey(currentUser.id)],
        queryFn: async () => await RecommendedActionsCardApi.fetchPadletTvSessions(),
        onFetchSuccess: (response) => {
          const data = response.data as Array<JsonAPIResource<PadletTvSession>>
          padletTvSessions.value = data.map((session) => session.attributes)
          padletTvSessionsStatus.value = FetchJsonStatus.Completed
        },
      })
    } catch (e) {
      // With no padlet.tv sessions the card recommends Padlet 101 and the Learn page.
      captureFetchException(e, { source: 'DashRecommendedActionsCardStore' })
      padletTvSessionsStatus.value = FetchJsonStatus.Error
    }
  }

  async function convertGoal(goal: RecommendedActionsCardGoal): Promise<void> {
    if (goal === 'viewed_home') {
      if (hasConvertedViewedHome.value) return
      hasConvertedViewedHome.value = true
    }
    try {
      await AbApi.convertAbExperimentGoal(EXPERIMENT_NAME, goal)
    } catch (e) {
      captureFetchException(e, { source: 'DashRecommendedActionsCardStoreConvertGoal', goal })
      if (goal === 'viewed_home') {
        hasConvertedViewedHome.value = false
        retryViewedHomeLater()
      }
    }
  }

  function retryViewedHomeLater(): void {
    const delay = VIEWED_HOME_RETRY_DELAYS_MS[viewedHomeRetries]
    if (delay === undefined) return
    viewedHomeRetries += 1
    setTimeout(() => void convertGoal('viewed_home'), delay)
  }

  // Shows the action at `nextCursor` straight away, then saves the cursor so later visits start there. A failed
  // request keeps the card moved on for the page, and the next page load uses the server's cursor.
  async function reload(nextCursor: string): Promise<void> {
    reloads.value += 1
    cursor.value = nextCursor
    pendingReloadSave = pendingReloadSave.then(async () => {
      try {
        await RecommendedActionsCardApi.reload(nextCursor)
      } catch (e) {
        captureFetchException(e, { source: 'DashRecommendedActionsCardStoreReload' })
      }
    })
    await pendingReloadSave
  }

  // Hides the card straight away; the server then keeps it hidden on every device and later visit. A failed
  // request keeps it hidden for the page, and it shows again on the next load.
  async function dismiss(): Promise<void> {
    xRecommendedActionsCard.value = false
    try {
      await RecommendedActionsCardApi.dismiss()
    } catch (e) {
      captureFetchException(e, { source: 'DashRecommendedActionsCardStoreDismiss' })
    }
  }

  return {
    xRecommendedActionsCard,
    isExposedInCurrentAccount,
    xRecommendedActionsCardInCurrentAccount,
    activeDay,
    reloads,
    cursor,
    padletTvSessions,
    padletTvSessionsStatus,
    shownSequences,
    hasTrackedView,
    initialize,
    fetchPadletTvSessions,
    convertGoal,
    reload,
    dismiss,
  }
})

export { useDashRecommendedActionsCardStore }
